/**
 * SINGLE SOURCE OF TRUTH FOR ALL OFFER CATEGORIES
 * 
 * This file contains the centralized configuration for all offer types in the web shop.
 * To add a new offer category, simply add a new entry to the OFFERS_CONFIG object below.
 * 
 * HOW TO ADD A NEW OFFER CATEGORY:
 * 1. Add a new entry to OFFERS_CONFIG with the following properties
 * 2. Import the category icon image at the top of this file
 * 3. That's it! The system will automatically handle everything else
 * 
 * Each offer configuration should include:
 * - id: Numeric identifier for the offer type (must match backend)
 * - key: String key used in code (state variables, API responses)
 * - displayName: Human-readable name shown in UI
 * - icon: Image icon for the category
 * - isVisible: Default visibility in filters
 * - isNew: Show "new" badge in UI
 * - renderType: How to render ('carousel', 'normal', 'custom', 'gambit')
 * - hasEndTime: Whether offers in this category have expiration times
 * - isEventPass: Special handling for event pass offers
 * - apiResponseKey: Key used in getWebShopInfo API response
 * - stateKey: Key used for component state
 * - order: Display order in the shop (lower = higher priority)
 */

import CI_SPECIAL_OFFERS from '../assets/images/category_icons/ci_special_offers.png';
import CI_DAILY_OFFERS from '../assets/images/category_icons/ci_daily_offers.png';
import CI_GEMS from '../assets/images/category_icons/ci_gems.png';
import CI_CRATES from '../assets/images/category_icons/ci_crates.png';
import CI_RESOURCES from '../assets/images/category_icons/ci_resources.png';
import CI_EVENT_PASS from '../assets/images/category_icons/ci_event_pass.png';
import CI_WEB_SHOP_EXCLUSIVE_OFFERS from '../assets/images/category_icons/ci_exclusive_offers.png';
import CI_COMMANDER_OFFERS from '../assets/images/category_icons/ci_commander.png';
import CI_SPECIAL_EVENTS from '../assets/images/category_icons/ci_special_events.png';

/**
 * Master configuration object for all offer categories
 * This is the ONLY place you need to edit to add/modify offer categories
 */
export const OFFERS_CONFIG = {
    // SPECIAL EVENTS is a UI *group*: it owns a single filter chip / scroll anchor
    // and consolidates its member sub-offers (flares, fortuneEventTokens,
    // gambitLeaguePackages) into one section rendered by ShopOffers.
    // It has no API/state key of its own — members below carry `groupKey: 'specialEvents'`.
    specialEvents: {
        id: 999,
        key: 'specialEvents',
        displayName: 'Special Events',
        icon: CI_SPECIAL_EVENTS,
        isVisible: true,
        isNew: true,
        renderType: 'specialEvents', // consolidated group rendering
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: null,
        stateKey: null,
        order: 1,
        isGroup: true,
        subKeys: ['flares', 'fortuneEventTokens', 'gambitLeaguePackages'],
    },
    flares: {
        id: 27,
        key: 'flares',
        displayName: 'Flares',
        icon: CI_SPECIAL_EVENTS,
        isVisible: true,
        isNew: true,
        renderType: 'flares', // custom flares rendering (mascot-driven art)
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: 'MascotDropsTokens',
        stateKey: 'flares',
        order: 1,
        groupKey: 'specialEvents', // rendered inside the Special Events group
        excludeFromFilters: true,  // no standalone filter chip
    },
    fortuneEventTokens: {
        id: 25,
        key: 'fortuneEventTokens',
        displayName: 'Fortune Event Tokens',
        icon: CI_SPECIAL_EVENTS,
        isVisible: true,
        isNew: true,
        renderType: 'custom', // custom rendering for fortune tokens
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: 'FortuneEventTokens',
        stateKey: 'fortuneEventTokens',
        order: 2,
        groupKey: 'specialEvents', // rendered inside the Special Events group
        excludeFromFilters: true,  // no standalone filter chip
    },
    gambitLeaguePackages: {
        id: 24,
        key: 'gambitLeaguePackages',
        displayName: 'Gambit League Packages',
        icon: CI_SPECIAL_EVENTS,
        isVisible: true,
        isNew: true,
        renderType: 'gambit', // special gambit rendering
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: 'GambitLeaguePackages',
        stateKey: 'gambitLeaguePackages',
        order: 3,
        groupKey: 'specialEvents', // rendered inside the Special Events group
        excludeFromFilters: true,  // no standalone filter chip
    },
    webShopExclusiveOffers: {
        id: 18,
        key: 'webShopExclusiveOffers',
        displayName: 'Web Shop Exclusive Offers',
        icon: CI_WEB_SHOP_EXCLUSIVE_OFFERS,
        isVisible: true,
        isNew: false,
        renderType: 'normal',
        hasEndTime: true,
        isEventPass: false,
        apiResponseKey: 'WebShopExclusiveOffers',
        stateKey: 'webShopExclusiveOffers',
        order: 3,
    },
    eventPassOffers: {
        id: 17,
        key: 'eventPassOffers',
        displayName: 'Event Pass',
        icon: CI_EVENT_PASS, // Can be overridden dynamically
        isVisible: true,
        isNew: false,
        renderType: 'normal',
        hasEndTime: true,
        isEventPass: true,
        apiResponseKey: 'BattlePassOffers',
        stateKey: 'eventPassOffers',
        order: 4,
        requiresFormatting: true, // Special flag for formatEventPassOffers
    },
    specialOffers: {
        id: 7,
        key: 'specialOffers',
        displayName: 'Special Offers',
        icon: CI_SPECIAL_OFFERS,
        isVisible: true,
        isNew: false,
        renderType: 'normal',
        hasEndTime: true,
        isEventPass: false,
        apiResponseKey: 'ShopOffers',
        stateKey: 'specialOffers',
        order: 5,
    },
    everyDayOffers: {
        id: 9,
        key: 'everyDayOffers',
        displayName: 'Every Day Offers',
        icon: CI_DAILY_OFFERS,
        isVisible: true,
        isNew: false,
        renderType: 'normal',
        hasEndTime: true,
        isEventPass: false,
        apiResponseKey: 'EveryDayOffers',
        stateKey: 'everyDayOffers',
        order: 6,
    },
    gems: {
        id: 8,
        key: 'gems',
        displayName: 'Gems',
        icon: CI_GEMS,
        isVisible: true,
        isNew: false,
        renderType: 'custom', // custom rendering for gems
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: 'GemsPackages',
        stateKey: 'gems',
        order: 7,
    },
    crates: {
        id: 13,
        key: 'crates',
        displayName: 'Crates',
        icon: CI_CRATES,
        isVisible: true,
        isNew: false,
        renderType: 'custom', // custom rendering for crates
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: 'Crates',
        stateKey: 'crates',
        order: 8,
    },
    resources: {
        id: 12,
        key: 'resources',
        displayName: 'Resources',
        icon: CI_RESOURCES,
        isVisible: true,
        isNew: false,
        renderType: 'custom', // custom rendering for resources
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: 'ExpeditionResources',
        stateKey: 'resources',
        order: 9,
    },
    commanders: {
        id: 23,
        key: 'commanders',
        displayName: 'Commanders',
        icon: CI_COMMANDER_OFFERS,
        isVisible: true,
        isNew: false,
        renderType: 'custom', // custom rendering for commanders
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: 'CommanderOffers',
        stateKey: 'commanderOffers',
        order: 10,
    },
    freeOffers: {
        id: null, // Free offers don't have a type ID
        key: 'freeOffers',
        displayName: 'Free Offers',
        icon: null,
        isVisible: false, // Not shown in filters
        isNew: false,
        renderType: 'free',
        hasEndTime: false,
        isEventPass: false,
        apiResponseKey: 'FreeOffers',
        stateKey: 'freeOffers',
        order: 0, // Shows first
        excludeFromFilters: true,
    },
};

/**
 * Utility functions to derive all needed constants from OFFERS_CONFIG
 */

// Get all offer configurations sorted by order
export const getOrderedOffers = () => {
    return Object.values(OFFERS_CONFIG).sort((a, b) => a.order - b.order);
};

// Get offer config by ID
export const getOfferConfigById = (id) => {
    return Object.values(OFFERS_CONFIG).find(config => config.id === id);
};

// Get offer config by key
export const getOfferConfigByKey = (key) => {
    return OFFERS_CONFIG[key];
};

// Get offer config by API response key
export const getOfferConfigByApiKey = (apiKey) => {
    return Object.values(OFFERS_CONFIG).find(config => config.apiResponseKey === apiKey);
};

// Get the ordered sub-offer configs that belong to a group (e.g. 'specialEvents')
export const getSubOffersByGroup = (groupKey) => {
    return Object.values(OFFERS_CONFIG)
        .filter(config => config.groupKey === groupKey)
        .sort((a, b) => a.order - b.order);
};

// Generate OFFER_TYPES object (id mappings)
export const OFFER_TYPES = Object.entries(OFFERS_CONFIG).reduce((acc, [key, config]) => {
    if (config.id !== null) {
        acc[key] = config.id;
    }
    return acc;
}, {});

// Generate OFFER_TYPE_TO_NAME (id -> display name)
export const OFFER_TYPE_TO_NAME = Object.values(OFFERS_CONFIG).reduce((acc, config) => {
    if (config.id !== null) {
        acc[config.id] = config.displayName;
    }
    return acc;
}, {});

// Generate OFFER_TYPE_TO_CONFIG_NAME (id -> key)
export const OFFER_TYPE_TO_CONFIG_NAME = Object.values(OFFERS_CONFIG).reduce((acc, config) => {
    if (config.id !== null) {
        acc[config.id] = config.key;
    }
    return acc;
}, {});

// Generate OFFER_NAME_TO_CONFIG_TYPE (key -> id)
export const OFFER_NAME_TO_CONFIG_TYPE = Object.entries(OFFERS_CONFIG).reduce((acc, [key, config]) => {
    if (config.id !== null) {
        acc[key] = config.id;
    }
    return acc;
}, {});

// Generate OFFER_TYPE_TO_CONFIG_ICON (id -> icon)
export const OFFER_TYPE_TO_CONFIG_ICON = Object.values(OFFERS_CONFIG).reduce((acc, config) => {
    if (config.id !== null && config.icon) {
        acc[config.id] = config.icon;
    }
    return acc;
}, {});

// Generate initial filters/categories state
export const generateInitialCategories = () => {
    return Object.entries(OFFERS_CONFIG).reduce((acc, [key, config]) => {
        if (config.id !== null && !config.excludeFromFilters) {
            acc[key] = {
                type: config.id,
                isVisible: config.isVisible,
                isNew: config.isNew,
            };
        }
        return acc;
    }, {});
};

// Generate offers object structure for API response mapping
export const mapApiResponseToOffers = (apiResponse) => {
    const offers = {};
    
    Object.values(OFFERS_CONFIG).forEach(config => {
        // Skip group/pseudo configs that own no state (e.g. Special Events)
        if (!config.stateKey) {
            return;
        }

        if (config.apiResponseKey && apiResponse[config.apiResponseKey]) {
            let data = apiResponse[config.apiResponseKey];
            
            // Special handling for event pass offers that need formatting
            if (config.requiresFormatting && config.key === 'eventPassOffers') {
                // formatEventPassOffers should be imported and called here
                // For now, we'll just pass the data through
                data = apiResponse[config.apiResponseKey];
            }
            
            offers[config.stateKey] = data || (config.renderType === 'custom' ? {} : []);
        } else {
            // Set default values
            offers[config.stateKey] = config.renderType === 'custom' ? {} : [];
        }
    });
    
    return offers;
};

// Get filterable categories (excludes those marked excludeFromFilters)
export const getFilterableCategories = () => {
    return Object.values(OFFERS_CONFIG).filter(config => !config.excludeFromFilters && config.id !== null);
};

// Check if offer type should use carousel on mobile
export const shouldUseCarousel = (offerKey, isMobile) => {
    const config = OFFERS_CONFIG[offerKey];
    if (!config) return false;
    
    // Only carousel-compatible render types show carousel on mobile
    return isMobile && (config.renderType === 'normal' || config.renderType === 'gambit' || config.renderType === 'free');
};

/**
 * Example of adding a new offer category:
 * 
 * newCategoryName: {
 *     id: 26,                                    // New unique ID from backend
 *     key: 'newCategoryName',                    // Same as the object key
 *     displayName: 'New Category',               // UI display name
 *     icon: CI_NEW_CATEGORY,                     // Import icon at top
 *     isVisible: true,                           // Show in filters by default
 *     isNew: true,                               // Show "new" badge
 *     renderType: 'normal',                      // or 'custom', 'gambit', 'free'
 *     hasEndTime: true,                          // Offers have expiration
 *     isEventPass: false,                        // Not an event pass
 *     apiResponseKey: 'NewCategoryOffers',       // Key in API response
 *     stateKey: 'newCategoryOffers',             // State variable name
 *     order: 1,                                  // Display order (lower = first)
 * }
 */
